/* eslint-disable no-param-reassign */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import extend from 'extend';

import comparisonDaterangeToString from '../../../../util/comparison-daterange-to-string';

import config from '../../../../../run-config';

import './highcharts-visualizer.css';

const weatherIcons = require.context('../../../../../images/reportingIcons/weatherIcons', true);


require('highcharts/highcharts-more.js')(Highcharts);
require('highcharts/modules/drilldown.js')(Highcharts);


class HighChartsVisualizer extends Component {
  componentDidMount() {
    this.chartRef = React.createRef();
  }

  componentDidUpdate() {
    if (this.chartRef && this.chartRef.current && this.chartRef.current.chart) {
      this.chartRef.current.chart.reflow();
    }
  }

  xAxisOptionsForIndex(i) {
    return {
      tickInterval: 3600 * 1000,
      type: 'datetime',
      dateTimeLabelFormats: {
        day: '%b %e',
      },
      visible: i === 0,
    };
  }

  pointMapping(s, point) {
    const { tooltipOptions } = point.series;
    const { valueDecimals, valueSuffix } = tooltipOptions;

    return `${s}<br /><span style="color:${point.color}">\u25CF</span>
    ${point.series.name}: <b>${valueDecimals ? point.y.toFixed(valueDecimals) : point.y}${valueSuffix}</b>`;
  }

  render() {
    const { options, comparisonDateRanges } = this.props;
    let { data } = this.props;

    Highcharts.setOptions({
      global: {
        useUTC: false,
      },
    });

    Highcharts.setOptions({
      lang: {
        months: ['Jänner', 'Februar', 'März', 'April', 'Mai',
          'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
        weekdays: ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'],
        shortMonths: ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sept', 'Okt', 'Nov', 'Dez'],
      },
    });

    // data can be nested in an array, depending on the data-transformer
    if (Array.isArray(data[0])) {
      data = data.flat();
    }

    const tooltipSeries = options.customTooltipColumn ? data.filter(d => d.name === options.customTooltipColumn)[0].data : null;

    const { pointMapping } = this;

    const highchartOptions = {
      chart: {
        backgroundColor: '#fefefe',
        zoomType: 'x',
      },
      title: {
        text: '',
      },
      yAxis: {
        title: {
          text: '',
        },
      },
      credits: {
        enabled: false,
      },
      colors: config.colors,
      series: data.filter(d => !options.customTooltipColumn || d.name !== options.customTooltipColumn).map((d) => {
        if (d.series) {
          const result = d.series;
          result.comparison = d.comparison;
          return result;
        }

        return d;
      }),
      tooltip: {
        useHTML: true,
        shared: true,
        formatter(defTooltip) {
          if (!options.customTooltipColumn) {
            return defTooltip.defaultFormatter.call(this, defTooltip);
          }

          const tooltipData = tooltipSeries.find(x => x[0] === this.x)[1];
          const icon = typeof tooltipData === 'string' ? tooltipData.substring(1) : null;

          const image = icon ? weatherIcons(`./${icon}.png`).default : null;
          const imageElement = icon
            ? `<image src="${image}" alt="${image}" width="30" height="30" class="tooltip-icon" />` : null;

          const heading = `<b>${Highcharts.dateFormat('%A, %b %e, %Y', this.x)}</b>`;

          const points = this.points.reduce(pointMapping, '');

          const tooltip = `
          <div>
            ${heading}
            ${imageElement || ''}
            ${points}
          </div>`;
          return tooltip;
        },
      },
    };


    if (data[0].drilldown) {
      highchartOptions.drilldown = data[0].drilldown;
      highchartOptions.drilldown.tooltip = highchartOptions.tooltip;
    }

    extend(true, highchartOptions, options);


    const { series } = highchartOptions;

    // append compare info to name if series flagged as comparison
    series.forEach((s, i) => {
      if (s.comparison) {
        if (!s.name) {
          s.name = series[0].name;
        }

        s.name += ` (${comparisonDaterangeToString(comparisonDateRanges[i - 1] || {})})`;
        s.stack = 'compare';
      }
      if (s && s.meta && s.meta.location && s.meta.location.name) {
        s.name += ` (${s.meta.location.name})`;
      }
    });

    const multipleSeriesAvailable = series.length > 1;

    const dataAvailable = series[0].data;
    const comparisonDataAvailable = series[series.length - 1].data;
    const seriesHaveDifferentTime = multipleSeriesAvailable
                                    && dataAvailable.length
          && comparisonDataAvailable && comparisonDataAvailable.length
          && series[0].data[0].x !== comparisonDataAvailable[0].x;

    if (seriesHaveDifferentTime) {
      if (options.xAxis.type === 'datetime') {
        highchartOptions.xAxis = [];
        series.forEach((s, i) => {
          s.xAxis = i;
          highchartOptions.xAxis.push(this.xAxisOptionsForIndex(i));
        });
      } else {
        highchartOptions.xAxis = { type: 'category' };
      }
    }

    return <HighchartsReact highcharts={Highcharts} options={highchartOptions} ref={this.chartRef} />;
  }
}

HighChartsVisualizer.propTypes = {
  comparisonDateRanges: PropTypes.array.isRequired,
  data: PropTypes.array,
  options: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.array,
  ]),
};

HighChartsVisualizer.defaultProps = {
  data: [],
  options: [],
};

export default HighChartsVisualizer;
